import React, { useMemo, useState } from 'react';
import { Box, Button, Modal, SpaceBetween, Spinner } from '@amzn/awsui-components-core';
import {
  ContentRequest,
  ContentRequestState,
  ContentRequestType,
  UpdateContentRequestResponse,
} from '@amzn/awscat-pattern-library-service-model';

import constants from '../../common/constants';
import ModalDialogButtonV3 from '../common/ModalDialogButtonV3';
import UserIdAutosuggest from '../common/UserIdAutosuggest';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { IMplServiceClient } from '../../api/IMplServiceClient';

interface ContentRequestAssignmentActionsProps {
  contentRequest: ContentRequest;
  handleSuggestionUpdate: (
    contentRequestId: string,
    state: ContentRequestState,
    assigneeId?: string,
  ) => void;
  userId: string;
  isAdmin: boolean;
  isBarRaiser: boolean;
  isTechWriter: boolean;
  isVerticalView?: boolean;
  handleSuggestionDelete?: (requestId: string) => void;
}

enum ReassignWarningType {
  AssignToOther = 'AssignToOther',
  SelfAssign = 'SelfAssign',
}

const ContentRequestAssignmentActions = ({
  contentRequest,
  handleSuggestionUpdate,
  userId,
  isAdmin,
  isBarRaiser,
  isTechWriter,
  isVerticalView,
  handleSuggestionDelete,
}: ContentRequestAssignmentActionsProps) => {
  // @ts-expect-error TS2345 - strict migration
  const [reassignWarningType, setReassignWarningType] = useState<ReassignWarningType>(null);
  const [isSelfAssigning, setAssigneeState] = useState<boolean>(false);
  const [newAssigneeId, setNewAssigneeId] = useState<string>('');
  const [showAssignToUser, setShowAssignToUser] = useState<boolean>(false);
  const [isAssigningToUser, setIsAssigningToUser] = useState<boolean>(false);
  const [isUpdatingContentRequest, setIsUpdatingContentRequest] = useState<boolean>(false);
  const [isDeletingContentRequest, setIsDeletingContentRequest] = useState<boolean>(false);
  const [contentOwnerWarning, setContentOwnerWarning] = useState<boolean>(false);
  const { assigneeName } = contentRequest;
  const mplService: IMplServiceClient = useMemo(() => MplServiceClientFactory.create(), []);

  const checkForReassignWarning = (): boolean => {
    const { state } = contentRequest;
    // @ts-expect-error TS2322 - strict migration
    return (
      assigneeName &&
      [
        ContentRequestState.ASSIGNED,
        ContentRequestState.ACCEPTED,
        ContentRequestState.IN_PROGRESS,
      ].includes(state)
    );
  };

  const checkContentOwner = async (assigningToUser: string): Promise<boolean> => {
    if (contentRequest.requestType === ContentRequestType.Update) {
      // @ts-expect-error TS2345 - strict migration
      const publication = await mplService.getPublication(contentRequest.contentId);
      // @ts-expect-error TS2345 - strict migration
      const { accessUsers } = await mplService.listContentSharedWith(contentRequest.contentId);
      return (
        publication.contentOwner.userId === assigningToUser ||
        accessUsers.some((us) => us.userId === assigningToUser)
      );
    }
    return false;
  };

  const expandAssignToUser = () => setShowAssignToUser(!showAssignToUser);
  // @ts-expect-error TS2345 - strict migration
  const hideReassignWarningModal = () => setReassignWarningType(null);
  const showContentOwnerWarningModal = () => setContentOwnerWarning(true);
  const hideContentOwnerWarningModal = () => setContentOwnerWarning(false);

  const assignToSelf = async () => {
    if (reassignWarningType) {
      hideReassignWarningModal();
    }
    const { requestId } = contentRequest;
    setAssigneeState(true);
    const contentOwner = await checkContentOwner(userId);
    if (contentOwner) {
      showContentOwnerWarningModal();
    } else {
      await handleSuggestionUpdate(requestId, ContentRequestState.ASSIGNED, userId);
    }
    setAssigneeState(false);
  };

  const handleSelfAssign = async () => {
    if (checkForReassignWarning()) {
      setReassignWarningType(ReassignWarningType.SelfAssign);
    } else {
      await assignToSelf();
    }
  };

  const handleAssigneeSelected = (
    selectedUserId: string,
    selectedUserName: string,
    validationError: any,
  ) => {
    if (!validationError) {
      setNewAssigneeId(selectedUserId);
    } else if (newAssigneeId) {
      setNewAssigneeId('');
    }
  };

  const handleConfirmAssignToUser = async () => {
    if (checkForReassignWarning()) {
      setReassignWarningType(ReassignWarningType.AssignToOther);
    } else {
      await assignToOtherUser();
    }
  };

  const assignToOtherUser = async () => {
    if (reassignWarningType) {
      hideReassignWarningModal();
    }
    const { requestId } = contentRequest;
    setIsAssigningToUser(true);
    const contentOwner = await checkContentOwner(newAssigneeId);
    if (contentOwner) {
      showContentOwnerWarningModal();
    } else {
      await handleSuggestionUpdate(requestId, ContentRequestState.ASSIGNED, newAssigneeId);
    }
    setIsAssigningToUser(false);
    setNewAssigneeId('');
    expandAssignToUser();
  };

  const renderContentOwnerWarningModal = () => {
    const actionButtons = (
      <Box float="right">
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="primary" onClick={hideContentOwnerWarningModal}>
            Ok
          </Button>
        </SpaceBetween>
      </Box>
    );
    return (
      <Modal
        onDismiss={hideContentOwnerWarningModal}
        visible={!!contentOwnerWarning}
        size="medium"
        footer={actionButtons}
        header={constants.CONTENT_REQUEST_OWNER_TITLE}
        closeAriaLabel="Close modal"
      >
        You cannot assign this suggestion to user who is already an author / co-author of this
        content.
      </Modal>
    );
  };

  const renderReassignWarningModal = () => {
    const handleConfirm =
      reassignWarningType === ReassignWarningType.SelfAssign ? assignToSelf : assignToOtherUser;
    const actionButtons = (
      <Box float="right">
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={hideReassignWarningModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirm}>
            Confirm
          </Button>
        </SpaceBetween>
      </Box>
    );
    return (
      <Modal
        onDismiss={hideReassignWarningModal}
        visible={!!reassignWarningType}
        size="medium"
        footer={actionButtons}
        header={constants.CONTENT_REQUEST_REASSIGN_TITLE}
        closeAriaLabel="Close modal"
      >
        This content suggestion was previously assigned to {assigneeName}. Do you want to cancel
        that assignment and reassign this suggestion to another author?
      </Modal>
    );
  };

  const handleConfirmSuggestionDelete = async () => {
    const { requestId } = contentRequest;
    setIsDeletingContentRequest(true);
    // @ts-expect-error TS2722 - strict migration
    await handleSuggestionDelete(requestId);
    setIsDeletingContentRequest(false);
  };

  const handleConfirmApprove = async () => {
    const { requestId } = contentRequest;
    setIsUpdatingContentRequest(true);
    await handleSuggestionUpdate(requestId, ContentRequestState.REQUESTED);
    setIsUpdatingContentRequest(false);
  };

  const handleConfirmReject = async () => {
    const { requestId } = contentRequest;
    setIsUpdatingContentRequest(true);
    await handleSuggestionUpdate(requestId, ContentRequestState.REJECTED);
    setIsUpdatingContentRequest(false);
  };

  const renderActionItems = () => {
    const selfAssignButtonText = isSelfAssigning ? (
      <Spinner />
    ) : (
      constants.CONTENT_REQUEST_ASSIGN_SELF_BUTTON_TEXT
    );
    const enablePreauthorReview =
      contentRequest.state === ContentRequestState.IN_REVIEW && (isAdmin || isBarRaiser);
    const enableAssignment =
      isAdmin || isTechWriter || contentRequest.state === ContentRequestState.REQUESTED;
    const alreadyAssignedToSelf = userId === contentRequest.assigneeId;
    const reassignToSameAssignee = contentRequest.assigneeId === newAssigneeId;
    const actionButtonClassName = isVerticalView ? 'full-width-button' : '';
    const showAssignmentAction = ![
      ContentRequestState.COMPLETED,
      ContentRequestState.DUPLICATED,
      ContentRequestState.REJECTED,
    ].includes(contentRequest.state);
    const assignToUserActions = showAssignToUser ? (
      <>
        <UserIdAutosuggest onUserSelected={handleAssigneeSelected} />
        <Button
          className={actionButtonClassName}
          onClick={handleConfirmAssignToUser}
          disabled={!newAssigneeId || reassignToSameAssignee}
        >
          <Box textAlign="center">{isAssigningToUser ? <Spinner /> : 'Confirm'}</Box>
        </Button>
      </>
    ) : null;
    const selfAssignActions =
      showAssignmentAction && !enablePreauthorReview ? (
        <>
          <Button
            className={actionButtonClassName}
            onClick={handleSelfAssign}
            disabled={!enableAssignment || alreadyAssignedToSelf}
          >
            <Box textAlign="center">{selfAssignButtonText}</Box>
          </Button>
          <Button
            className={actionButtonClassName}
            onClick={expandAssignToUser}
            disabled={!enableAssignment}
          >
            <Box textAlign="center">{constants.CONTENT_REQUEST_ASSIGN_OTHER_BUTTON_TEXT}</Box>
          </Button>
        </>
      ) : null;
    const approveButton = enablePreauthorReview ? (
      <ModalDialogButtonV3
        buttonText={constants.CONTENT_REQUESTS_APPROVE_LABEL}
        header={constants.CONTENT_REQUESTS_APPROVE_LABEL}
        text={constants.CONTENT_REQUESTS_APPROVE_TEXT}
        onSubmit={handleConfirmApprove}
        submitButtonText={'Approve'}
        fullwidth
        loading={isUpdatingContentRequest}
      />
    ) : null;
    const rejectButton = enablePreauthorReview ? (
      <ModalDialogButtonV3
        buttonText={constants.CONTENT_REQUESTS_REJECT_LABEL}
        header={constants.CONTENT_REQUESTS_REJECT_LABEL}
        text={constants.CONTENT_REQUESTS_REJECT_TEXT}
        onSubmit={handleConfirmReject}
        submitButtonText={'Reject'}
        fullwidth
        loading={isUpdatingContentRequest}
      />
    ) : null;
    const showDeletionButton = isAdmin || userId === contentRequest.userId;
    const deletionButton = showDeletionButton ? (
      <ModalDialogButtonV3
        buttonText={constants.CONTENT_REQUESTS_DELETION_LABEL}
        header={constants.CONTENT_REQUESTS_DELETION_LABEL}
        text={constants.CONTENT_REQUESTS_DELETION_TEXT}
        onSubmit={handleConfirmSuggestionDelete}
        submitButtonText={'Delete'}
        fullwidth
        loading={isDeletingContentRequest}
      />
    ) : null;

    if (isVerticalView) {
      return (
        <div className="cr-list-view-actions">
          {renderContentOwnerWarningModal()}
          {renderReassignWarningModal()}
          <SpaceBetween id="suggestion-list-actions-vertical" direction="vertical" size="xxs">
            {selfAssignActions}
            {approveButton}
            {rejectButton}
            {deletionButton}
            <br />
            {assignToUserActions}
          </SpaceBetween>
        </div>
      );
    }

    return (
      <SpaceBetween direction="vertical" size="s">
        {renderContentOwnerWarningModal()}
        {renderReassignWarningModal()}
        <SpaceBetween direction="horizontal" size="s">
          {selfAssignActions}
          {approveButton}
          {rejectButton}
        </SpaceBetween>
        <SpaceBetween direction="vertical" size="xxs">
          {assignToUserActions}
        </SpaceBetween>
      </SpaceBetween>
    );
  };

  return <>{renderActionItems()}</>;
};

export default ContentRequestAssignmentActions;
